import React, { useEffect, useMemo, useRef, useState } from 'react';
import s from './ConfigPicker.module.sass';
import FlagIcon from '../FlagIcon/FlagIcon';
import { getFlagInfo } from '../../config/flags';
import { getCachedConfigFlags, loadConfigFlags } from '../../utils/configFlagCache';

// Дропдаун выбора конфига с группировкой по странам:
// сверху поиск, ниже — сворачиваемые секции «страна» (флаг + название + число
// конфигов) в алфавитном порядке. Открытие/закрытие управляется родителем
// (админкой), чтобы не наслаиваться на другие дропдауны.
// variant: 'nova' — токены темы Nova, 'classic' — светлая палитра обычных тем.
const ConfigPicker = ({ configs, value, onSelect, open, onToggle, onClose, variant = 'classic' }) => {
    // Кэш флагов из localStorage/сети (для fallback и других вкладок)
    const [cachedFlags, setCachedFlags] = useState(getCachedConfigFlags);
    const [query, setQuery] = useState('');
    const [expanded, setExpanded] = useState(() => new Set());
    const searchRef = useRef(null);

    // Поиск имеет смысл только при заметном списке; при 1–4 конфигах прячем
    const showSearch = (configs || []).length >= 5;

    // Карта «файл → код страны». Приоритет — флаг ИЗ списка конфигов (сервер
    // теперь отдаёт его сразу, без догрузки каждого файла), поверх кладём
    // кэш на случай, если для какого-то конфига флага в списке нет.
    const flags = useMemo(() => {
        const m = { ...cachedFlags };
        (configs || []).forEach((c) => {
            const fn = c.filename || c;
            if (c && c.flag) m[fn] = c.flag;
        });
        return m;
    }, [configs, cachedFlags]);

    // Ревалидируем кэш флагов при изменении списка (новый/удалённый конфиг)
    useEffect(() => {
        loadConfigFlags().then(setCachedFlags);
    }, [configs]);

    // При каждом открытии: чистим поиск, раскрываем группу выбранного конфига
    useEffect(() => {
        if (!open) return;
        setQuery('');
        if (!showSearch) {
            // Маленький список: раскрываем всё сразу, кликать по хайдам незачем
            const all = new Set((configs || []).map((c) => flags[c.filename || c] || 'unknown'));
            setExpanded(all);
            return;
        }
        const code = value ? (flags[value] || 'unknown') : null;
        setExpanded(code ? new Set([code]) : new Set());
        const id = requestAnimationFrame(() => searchRef.current?.focus());
        return () => cancelAnimationFrame(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const q = query.trim().toLowerCase();

    // Группы: код страны → { code, label, items[] }, сортировка по названию
    // страны, «Без страны» — всегда в конце
    const groups = useMemo(() => {
        const byCode = new Map();
        (configs || []).forEach((config) => {
            const filename = config.filename || config;
            const name = config.name || String(filename).replace('.json', '');
            const code = flags[filename] || 'unknown';
            if (!byCode.has(code)) {
                byCode.set(code, {
                    code,
                    label: code === 'unknown' ? 'Без страны' : getFlagInfo(code).name,
                    items: [],
                });
            }
            byCode.get(code).items.push({ filename, name });
        });
        const list = Array.from(byCode.values());
        list.forEach((g) => g.items.sort((a, b) => a.name.localeCompare(b.name, 'ru')));
        list.sort((a, b) => {
            if (a.code === 'unknown') return 1;
            if (b.code === 'unknown') return -1;
            return a.label.localeCompare(b.label, 'ru');
        });
        if (!q) return list;
        // Поиск: по имени конфига и по названию страны; пустые группы прячем
        return list
            .map((g) => ({
                ...g,
                items: g.label.toLowerCase().includes(q)
                    ? g.items
                    : g.items.filter((it) => it.name.toLowerCase().includes(q)),
            }))
            .filter((g) => g.items.length > 0);
    }, [configs, flags, q]);

    const toggleGroup = (code) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(code)) next.delete(code); else next.add(code);
            return next;
        });
    };

    const selectedName = useMemo(() => {
        if (!value) return '';
        const info = (configs || []).find((c) => (c.filename || c) === value);
        return info?.name || String(value).replace('.json', '');
    }, [configs, value]);
    const selectedCode = value ? (flags[value] || 'unknown') : null;

    const pick = (filename) => {
        onClose?.();
        onSelect?.(filename);
    };

    return (
        <div className={`${s.dropdown} ${variant === 'nova' ? s.nova : s.classic}`}>
            <button type="button" className={s.dropdownBtn} onClick={onToggle}>
                <span className={s.btnValue}>
                    {selectedCode && <FlagIcon code={selectedCode} className={s.flag} alt="" />}
                    <span className={s.btnText}>{selectedName || '— не выбран —'}</span>
                </span>
                <svg className={`${s.chevron} ${open ? s.chevronOpen : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
            {open && (
                <div className={s.dropdownMenu}>
                    {showSearch && (
                    <div className={s.searchWrap}>
                        <svg className={s.searchIcon} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                            <circle cx="11" cy="11" r="7" />
                            <line x1="21" y1="21" x2="16.5" y2="16.5" />
                        </svg>
                        <input
                            ref={searchRef}
                            className={s.searchInput}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Поиск конфига или страны…"
                            onKeyDown={(e) => { if (e.key === 'Escape') onClose?.(); }}
                        />
                        {query && (
                            <button type="button" className={s.searchClear} onClick={() => { setQuery(''); searchRef.current?.focus(); }} aria-label="Очистить">
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        )}
                    </div>
                    )}
                    <div className={s.groupList}>
                        {groups.length === 0 && (
                            <div className={s.empty}>Ничего не найдено</div>
                        )}
                        {groups.map((g) => {
                            // Во время поиска все совпавшие группы раскрыты
                            const isOpen = q ? true : expanded.has(g.code);
                            // Если в стране всего один конфиг — раскрывать нечего:
                            // клик по самой стране сразу выбирает этот конфиг.
                            const single = g.items.length === 1;
                            const onlyItem = single ? g.items[0] : null;
                            const headActive = single && onlyItem.filename === value;
                            return (
                                <div key={g.code} className={s.group}>
                                    <button
                                        type="button"
                                        className={`${s.groupHead} ${headActive ? s.groupHeadActive : ''}`}
                                        onClick={() => (single ? pick(onlyItem.filename) : toggleGroup(g.code))}
                                    >
                                        <FlagIcon code={g.code} className={s.flag} alt="" />
                                        <span className={s.groupLabel}>{g.label}</span>
                                        {single ? (
                                            headActive && (
                                                <svg className={s.groupCheck} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M20 6 9 17l-5-5" />
                                                </svg>
                                            )
                                        ) : (
                                            <>
                                                <span className={s.groupCount}>{g.items.length}</span>
                                                <svg className={`${s.chevron} ${s.chevronSm} ${isOpen ? s.chevronOpen : ''}`} width="13" height="13" viewBox="0 0 24 24" fill="none">
                                                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </>
                                        )}
                                    </button>
                                    {/* Несколько конфигов — плавное раскрытие через grid-rows 0fr→1fr */}
                                    {!single && (
                                        <div className={`${s.groupBody} ${isOpen ? s.groupBodyOpen : ''}`}>
                                            <div className={s.groupInner}>
                                                {g.items.map((it) => (
                                                    <button
                                                        key={it.filename}
                                                        type="button"
                                                        className={`${s.item} ${it.filename === value ? s.itemActive : ''}`}
                                                        onClick={() => pick(it.filename)}
                                                    >
                                                        <span className={s.itemText}>{it.name}</span>
                                                        {it.filename === value && (
                                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M20 6 9 17l-5-5" />
                                                            </svg>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConfigPicker;
